<template>
  <ContentWrap>
    <el-form :inline="true" :model="queryParams" class="-mb-15px">
      <el-form-item label="活动" prop="activityId">
        <el-select
          v-model="queryParams.activityId"
          placeholder="请选择活动"
          clearable
          filterable
          class="!w-220px"
        >
          <el-option v-for="a in activityList" :key="a.id" :label="a.title" :value="a.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="业务单号" prop="orderNo">
        <el-input
          v-model="queryParams.orderNo"
          placeholder="单号"
          clearable
          class="!w-200px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" placeholder="请选择" clearable class="!w-130px">
          <el-option label="待支付" :value="0" />
          <el-option label="已支付" :value="1" />
          <el-option label="已取消" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="下单时间" prop="createTime">
        <el-date-picker
          v-model="queryParams.createTime"
          type="datetimerange"
          range-separator="-"
          start-placeholder="开始"
          end-placeholder="结束"
          value-format="YYYY-MM-DD HH:mm:ss"
          class="!w-350px"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery"><Icon icon="ep:search" /> 搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" /> 重置</el-button>
      </el-form-item>
    </el-form>

    <el-table v-loading="loading" :data="list" border>
      <el-table-column label="业务单号" prop="orderNo" min-width="200" show-overflow-tooltip />
      <el-table-column label="活动" prop="activityTitle" min-width="180" show-overflow-tooltip />
      <el-table-column label="报名人" prop="signupName" width="100" />
      <el-table-column label="手机" prop="signupMobile" width="120" />
      <el-table-column label="应付" prop="amount" width="90" align="right">
        <template #default="{ row }">¥{{ formatAmount(row.amount) }}</template>
      </el-table-column>
      <el-table-column label="实付" prop="paidAmount" width="90" align="right">
        <template #default="{ row }">¥{{ formatAmount(row.paidAmount) }}</template>
      </el-table-column>
      <el-table-column label="状态" prop="status" width="90" align="center">
        <template #default="{ row }">
          <el-tag :type="statusType(row.status)">{{ row.statusText || statusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="支付单" prop="payOrderId" width="100" />
      <el-table-column label="统一订单" prop="tradeOrderId" width="100" />
      <el-table-column label="支付时间" prop="payTime" width="170" :formatter="dateFormatter" />
      <el-table-column label="过期时间" prop="expireTime" width="170" :formatter="dateFormatter" />
      <el-table-column label="下单时间" prop="createTime" width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" align="center" width="90" fixed="right">
        <template #default="{ row }">
          <el-button
            link
            type="primary"
            v-hasPermi="['activity:signup-order:query']"
            @click="openDetail(row.id)"
          >
            详情
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="detailVisible" title="报名订单详情" width="560px">
    <el-descriptions v-if="detail" :column="1" border>
      <el-descriptions-item label="业务单号">{{ detail.orderNo }}</el-descriptions-item>
      <el-descriptions-item label="活动">{{ detail.activityTitle }}（#{{ detail.activityId }}）</el-descriptions-item>
      <el-descriptions-item label="报名记录">#{{ detail.signupId }}</el-descriptions-item>
      <el-descriptions-item label="报名人">{{ detail.signupName }} / {{ detail.signupMobile }}</el-descriptions-item>
      <el-descriptions-item label="会员编号">{{ detail.memberId }}</el-descriptions-item>
      <el-descriptions-item label="应付 / 实付"
        >¥{{ formatAmount(detail.amount) }} / ¥{{ formatAmount(detail.paidAmount) }}</el-descriptions-item
      >
      <el-descriptions-item label="状态">{{ detail.statusText || statusText(detail.status) }}</el-descriptions-item>
      <el-descriptions-item label="支付单 / 统一订单"
        >{{ detail.payOrderId || '-' }} / {{ detail.tradeOrderId || '-' }}</el-descriptions-item
      >
      <el-descriptions-item label="支付渠道">{{ payChannelText(detail.payChannel) }}</el-descriptions-item>
      <el-descriptions-item label="支付时间">{{ formatDetailTime(detail.payTime) }}</el-descriptions-item>
      <el-descriptions-item label="过期时间">{{ formatDetailTime(detail.expireTime) }}</el-descriptions-item>
      <el-descriptions-item label="下单时间">{{ formatDetailTime(detail.createTime) }}</el-descriptions-item>
    </el-descriptions>
    <template #footer>
      <el-button type="primary" @click="detailVisible = false">关闭</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { dateFormatter, formatDate } from '@/utils/formatTime'
import * as SignupOrderApi from '@/api/activity/signupOrder'
import * as ActivityApi from '@/api/activity/activity'
import type { ActivitySignupOrderVO } from '@/api/activity/signupOrder'

defineOptions({ name: 'ActivitySignupOrder' })

const message = useMessage()
const loading = ref(true)
const list = ref<ActivitySignupOrderVO[]>([])
const total = ref(0)
const activityList = ref<any[]>([])
const detailVisible = ref(false)
const detail = ref<ActivitySignupOrderVO | null>(null)

const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  activityId: undefined as number | undefined,
  orderNo: '',
  status: undefined as number | undefined,
  createTime: [] as string[]
})

const statusText = (status?: number) =>
  ({ 0: '待支付', 1: '已支付', 2: '已取消', 3: '已退款' })[status as number] || '-'
const statusType = (status?: number) =>
  ({ 0: 'warning', 1: 'success', 2: 'info', 3: 'danger' })[status as number] || 'info'

/** 支付渠道：后端存 trade 等英文码，详情展示中文 */
const payChannelText = (channel?: string) => {
  if (!channel) return '-'
  const map: Record<string, string> = {
    trade: '统一订单',
    wx_pub: '微信公众号',
    wx_lite: '微信小程序',
    wx_app: '微信 App',
    alipay_pc: '支付宝 PC',
    alipay_wap: '支付宝 WAP',
    alipay_app: '支付宝 App',
    wallet: '钱包余额'
  }
  return map[channel] || channel
}

/** 详情时间：时间戳 / 字符串 → YYYY-MM-DD HH:mm:ss（与列表 dateFormatter 一致） */
const formatDetailTime = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === '') return '-'
  return formatDate(value) || '-'
}

const formatAmount = (v: any) => {
  const n = Number(v)
  return Number.isFinite(n) ? n.toFixed(2) : '0.00'
}

const getList = async () => {
  loading.value = true
  try {
    const params: any = { ...queryParams }
    if (!params.createTime || params.createTime.length !== 2) {
      delete params.createTime
    }
    if (!params.orderNo) delete params.orderNo
    const data = await SignupOrderApi.getSignupOrderPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.activityId = undefined
  queryParams.orderNo = ''
  queryParams.status = undefined
  queryParams.createTime = []
  handleQuery()
}

const openDetail = async (id: number) => {
  try {
    detail.value = await SignupOrderApi.getSignupOrder(id)
    detailVisible.value = true
  } catch (e) {
    message.error('加载详情失败')
  }
}

onMounted(async () => {
  activityList.value = (await ActivityApi.getActivityListAll()) || []
  await getList()
})
</script>
